// Чистые TypeScript типы для клиента (без typebox).
// Должны быть зеркалом protocol.ts — каждое имя поля и каждый литерал union'а.

export const PROTOCOL_VERSION = 1;

export type PlayerId = string;
export type RoomId = string;
export type Team = 'red' | 'blue' | 'spectator';
export type PlayableTeam = 'red' | 'blue';
export type RoomPhase = 'lobby' | 'warmup' | 'active' | 'ended';

export const PLAYER_SKINS = [
  'cucumber', 'tomato', 'carrot', 'onion', 'beetroot',
  'chili', 'potato', 'eggplant', 'cornbob', 'celery',
] as const;

export type PlayerSkin = typeof PLAYER_SKINS[number];

export interface PlayerSnapshot {
  id: PlayerId;
  name: string;
  skin: PlayerSkin;
  team: Team;
  ready: boolean;
  connected: boolean;
  joinedAtMs: number;
  lastSeenAtMs: number;
}

export interface RoomState {
  roomId: RoomId;
  ownerId?: PlayerId;
  createdAtMs: number;
  updatedAtMs: number;
  phase: RoomPhase;
  players: PlayerSnapshot[];
  maxPlayers: number;
}

export interface GamePlayerState {
  x: number;
  y: number;
  vx: number;
  vy: number;
  facing: -1 | 1;
  alive: boolean;
  aimAngle: number;
  hp: number;
}

export interface GamePlayerSnapshot extends GamePlayerState {
  id: PlayerId;
  name: string;
  skin: PlayerSkin;
  team: Team;
  updatedAtMs: number;
}

export interface BulletState {
  id: string;
  ownerId: PlayerId;
  x: number;
  y: number;
  vx: number;
  vy: number;
  spawnTimeMs: number;
}

export interface Scores {
  red: number;
  blue: number;
}

export interface GameSnapshot {
  roomId: RoomId;
  serverTimeMs: number;
  players: GamePlayerSnapshot[];
  bullets: BulletState[];
  scores: Scores;
  matchTimeLeftMs: number;
  playerStats: MatchPlayerStats[];
}

export interface ChatMessage {
  id: string;
  roomId: RoomId;
  playerId: PlayerId;
  name: string;
  text: string;
  sentAtMs: number;
}

export interface CreateRoomRequest {
  maxPlayers?: number;
}

export interface CreateRoomResponse {
  room: RoomState;
}

export interface GetRoomResponse {
  room: RoomState;
}

export interface ListRoomsResponse {
  rooms: RoomState[];
}

// K/D статистика игрока в матче — синкается через сервер
export interface MatchPlayerStats {
  playerId: PlayerId;
  kills: number;
  deaths: number;
}

export interface MatchStatsUpdate {
  type: 'matchStats';
  stats: MatchPlayerStats[];
}

export type ClientMessage =
  | { type: 'ping'; clientTimeMs: number }
  | { type: 'hello'; resumePlayerId?: PlayerId }
  | { type: 'watchRooms' }
  | { type: 'createRoom'; name?: string; maxPlayers?: number }
  | { type: 'joinRoom'; roomId: RoomId; name?: string }
  | { type: 'leaveRoom' }
  | { type: 'deleteRoom' }
  | { type: 'setReady'; ready: boolean }
  | { type: 'selectTeam'; team: PlayableTeam | 'auto' }
  | { type: 'playerState'; state: GamePlayerState }
  | { type: 'playerShoot'; x: number; y: number; vx: number; vy: number; clientPingMs?: number; interpDelayMs?: number }
  | { type: 'setLevelSolids'; solids: Array<{ x: number; y: number; w: number; h: number }> }
  | { type: 'chat'; text: string };

export type RoomEvent =
  | { type: 'playerJoined'; playerId: PlayerId; roomId: RoomId }
  | { type: 'playerLeft'; playerId: PlayerId; roomId: RoomId }
  | { type: 'kill'; roomId: RoomId; killerId: PlayerId; victimId: PlayerId }
  | { type: 'damage'; roomId: RoomId; targetId: PlayerId; attackerId: PlayerId; hpAfter: number };

export type ServerMessage =
  | { type: 'welcome'; protocolVersion: typeof PROTOCOL_VERSION; playerId: PlayerId; serverTimeMs: number; room?: RoomState }
  | { type: 'pong'; clientTimeMs: number; serverTimeMs: number }
  | { type: 'roomState'; room: RoomState; chatHistory?: ChatMessage[] }
  | { type: 'roomsState'; rooms: RoomState[] }
  | { type: 'gameSnapshot'; snapshot: GameSnapshot }
  | { type: 'chatMessage'; message: ChatMessage }
  | { type: 'roomDeleted'; roomId: RoomId }
  | { type: 'event'; event: RoomEvent }
  | { type: 'error'; code: string; message: string };
